import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import './AdminLoyalty.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminLoyalty() {
  const queryClient = useQueryClient();
  const { success, error: showError, warning } = useNotification();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    points: 0,
    description: ''
  });
  const [contentLang, setContentLang] = useState('lt');
  const [contentForm, setContentForm] = useState({
    title_lt: '', title_en: '', title_de: '',
    description_lt: '', description_en: '', description_de: ''
  });

  const { data: pointsData, isLoading } = useQuery({
    queryKey: ['admin-loyalty-points', page, limit, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);

      const res = await apiClient.get(`/admin/loyalty/users?${params.toString()}`);
      return res.data;
    },
  });

  const { data: contentData } = useQuery({
    queryKey: ['admin-loyalty-content'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/loyalty/content');
      return res.data;
    },
  });

  useEffect(() => {
    if (contentData) {
      setContentForm({
        title_lt: contentData.title_lt ?? '',
        title_en: contentData.title_en ?? '',
        title_de: contentData.title_de ?? '',
        description_lt: contentData.description_lt ?? '',
        description_en: contentData.description_en ?? '',
        description_de: contentData.description_de ?? ''
      });
    }
  }, [contentData]);

  const saveContentMutation = useMutation({
    mutationFn: async (payload) => {
      await apiClient.put('/admin/loyalty/content', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-loyalty-content']);
      success('Lojalumo programos turinys išsaugotas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida išsaugant turinį');
    },
  });

  const handleSaveContent = () => {
    saveContentMutation.mutate(contentForm);
  };

  const { data: historyData } = useQuery({
    queryKey: ['loyalty-history', selectedUserId],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/loyalty/users/${selectedUserId}/history`);
      return res.data;
    },
    enabled: !!selectedUserId,
  });

  const adjustMutation = useMutation({
    mutationFn: async ({ userId, data }) => {
      return apiClient.post(`/admin/loyalty/users/${userId}/adjust`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-loyalty-points']);
      queryClient.invalidateQueries(['loyalty-history', selectedUserId]);
      setShowAdjustModal(false);
      setAdjustForm({ points: 0, description: '' });
      success('Lojalumo taškai sėkmingai atnaujinti');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida koreguojant lojalumo taškus');
    },
  });

  const handleAdjust = (user) => {
    setSelectedUserId(user.user_id);
    setAdjustForm({ points: 0, description: '' });
    setShowAdjustModal(true);
  };

  const handleSaveAdjust = () => {
    if (!adjustForm.points || parseInt(adjustForm.points) === 0) {
      warning('Nurodykite taškų kiekį');
      return;
    }

    adjustMutation.mutate({
      userId: selectedUserId,
      data: adjustForm
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('lt-LT');
  };

  if (isLoading) {
    return <div className="admin-loyalty-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-loyalty-page container">
      <div className="loyalty-header">
        <h1>Lojalumo taškai</h1>
      </div>

      {/* Programos turinys (pavadinimas / aprašymas trimis kalbomis) */}
      <div className="loyalty-content-card">
        <h3>Programos turinys</h3>
        <p className="admin-lang-hint">Pavadinimas ir aprašymas lojalumo programai – rodomas vartotojams. Užpildykite bent viena kalba (LT / EN / DE).</p>
        <div className="admin-lang-tabs">
          {LOCALES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`admin-lang-tab ${contentLang === code ? 'active' : ''}`}
              onClick={() => setContentLang(code)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="form-group">
          <label>Pavadinimas ({LOCALES.find(l => l.code === contentLang)?.label})</label>
          <input
            type="text"
            value={contentForm[`title_${contentLang}`] ?? ''}
            onChange={(e) => setContentForm({ ...contentForm, [`title_${contentLang}`]: e.target.value })}
            className="input"
            placeholder={contentLang === 'lt' ? 'Lojalumo programa' : contentLang === 'en' ? 'Loyalty program' : 'Treueprogramm'}
          />
        </div>
        <div className="form-group">
          <label>Aprašymas ({LOCALES.find(l => l.code === contentLang)?.label})</label>
          <textarea
            value={contentForm[`description_${contentLang}`] ?? ''}
            onChange={(e) => setContentForm({ ...contentForm, [`description_${contentLang}`]: e.target.value })}
            className="input"
            rows="4"
            placeholder={contentLang === 'lt' ? 'Trumpas programos aprašymas vartotojams...' : contentLang === 'en' ? 'Short program description for users...' : 'Kurze Programmbeschreibung...'}
          />
        </div>
        <div className="loyalty-content-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveContent}
            disabled={saveContentMutation.isLoading}
          >
            {saveContentMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti programos turinį'}
          </button>
        </div>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Paieška:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPage(1);
              }}
              className="input"
              placeholder="Ieškoti pagal el. paštą, vardą..."
            />
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({ search: '' });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      <div className="loyalty-table-wrapper">
        <table className="loyalty-table">
          <thead>
            <tr>
              <th>Vartotojas</th>
              <th>Dabartiniai taškai</th>
              <th>Viso uždirbta</th>
              <th>Viso išleista</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {pointsData?.points && pointsData.points.length > 0 ? (
              pointsData.points.map((point) => (
              <tr key={point.user_id}>
                <td>
                  {point.email}
                  {point.first_name && point.last_name && (
                    <div className="user-name">{point.first_name} {point.last_name}</div>
                  )}
                </td>
                <td>
                  <strong>{point.points || 0}</strong>
                </td>
                <td>{point.total_earned || 0}</td>
                <td>{point.total_spent || 0}</td>
                <td>
                  <button
                    onClick={() => {
                      setSelectedUserId(point.user_id);
                    }}
                    className="btn btn-sm btn-secondary"
                  >
                    Istorija
                  </button>
                  <button
                    onClick={() => handleAdjust(point)}
                    className="btn btn-sm btn-primary"
                  >
                    Koreguoti
                  </button>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                  Lojalumo taškų duomenų nėra
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Puslapiavimas */}
      {pointsData?.pagination && (
        <Pagination
          page={pointsData.pagination.page}
          totalPages={pointsData.pagination.pages}
          totalItems={pointsData.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Istorijos modalas */}
      {selectedUserId && !showAdjustModal && (
        <div className="modal-overlay" onClick={() => setSelectedUserId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Lojalumo taškų istorija</h2>
            {historyData?.history && historyData.history.length > 0 ? (
              <div className="history-table-wrapper">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Tipas</th>
                      <th>Taškai</th>
                      <th>Užsakymas</th>
                      <th>Aprašymas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.history.map((entry) => (
                      <tr key={entry.id}>
                        <td>{formatDate(entry.created_at)}</td>
                        <td>
                          {entry.transaction_type === 'earned' && 'Uždirbta'}
                          {entry.transaction_type === 'spent' && 'Išleista'}
                          {entry.transaction_type === 'expired' && 'Pasibaigė'}
                          {entry.transaction_type === 'adjusted' && 'Koreguota'}
                        </td>
                        <td className={entry.points > 0 ? 'positive' : 'negative'}>
                          {entry.points > 0 ? '+' : ''}{entry.points}
                        </td>
                        <td>{entry.order_number || (entry.order_id ? `#${entry.order_id}` : '—')}</td>
                        <td>{entry.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Istorijos nėra</p>
            )}
            <div className="modal-actions">
              <button onClick={() => setSelectedUserId(null)} className="btn btn-secondary">
                Uždaryti
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Koregavimo modalas */}
      {showAdjustModal && (
        <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Koreguoti lojalumo taškus</h2>

            <div className="form-group">
              <label>Taškų kiekis *</label>
              <input
                type="number"
                value={adjustForm.points}
                onChange={(e) => setAdjustForm({ ...adjustForm, points: parseInt(e.target.value) || 0 })}
                className="input"
                placeholder="Teigiamas = pridėti, neigiamas = atimti"
                required
              />
              <small>Teigiamas skaičius = pridėti taškus, neigiamas = atimti taškus</small>
            </div>

            <div className="form-group">
              <label>Aprašymas</label>
              <textarea
                value={adjustForm.description}
                onChange={(e) => setAdjustForm({ ...adjustForm, description: e.target.value })}
                className="input"
                rows="3"
                placeholder="Priežastis koregavimui"
              />
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowAdjustModal(false)} className="btn btn-secondary">
                Atšaukti
              </button>
              <button
                onClick={handleSaveAdjust}
                disabled={adjustMutation.isLoading || !adjustForm.points || parseInt(adjustForm.points) === 0}
                className="btn btn-primary"
              >
                {adjustMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLoyalty;
